import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';

import {
  deleteJobArtefact,
  downloadArtefact,
  getHypertestJobArtifacts,
  getHypertestJobArtifactsSilent,
  setCurrentArtefactClick,
  fetchArtefactContent,
} from '../store/actions/artifacts';

import history from 'utils/history';
import useToast from '../hooks/useToast';
import { messages } from '../utils/messages';
import { sendAmplitudeEvents } from '../utils/common';
import { EventNames } from '../utils/events';

const queryString = require('query-string');

const useArtifact = (props = {}) => {
  let {
    location: { search },
  } = history;
  const dispatch = useDispatch();
  let { handleShowToast } = useToast();

  const artifacts = useSelector((state) => state.artifacts?.artifacts);
  const currentArtefact = useSelector(
    (state) => state.artifacts?.currentArtefact
  );
  const visual = useSelector((state) => state.hypertestJobDetail?.visual);
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  const parsed = queryString.parse(search);
  let jobId = props.jobId || parsed.jobId;
  let setting = useSelector((state) => state.appSetting?.setting);

  const artefactContentStructure = useSelector(
    (state) => state.artifacts?.artefactContentStructure
  );
  let artefactReleaseDate = new Date(
    process.env.REACT_APP_ARTEFACT_FOLDER_STRUCTURE_RELEASE_DATE
  );

  const auth = useMemo(() => {
    return {
      username: userInfo.data && userInfo.data.username,
      password: userInfo.data && userInfo.data.apiToken,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.data?.username, userInfo?.data?.apiToken]);

  const getArtifacts = async () => {
    await dispatch(
      getHypertestJobArtifacts({
        auth,
        jobId,
      })
    );
  };
  const deleteArtefact = async (name) => {
    const res = await dispatch(
      deleteJobArtefact({
        auth,
        jobId,
        name,
      })
    );
    if (
      get(res, ['payload', 'response', 'data', 'status'], '') !== 'failed' ||
      get(res, ['payload', 'status'], '') !== 'failed'
    ) {
      handleShowToast(messages.MESSAGE.ARTEFACT_DELETED_SUCCESS, 'check');
      dispatch(
        getHypertestJobArtifactsSilent({
          auth,
          jobId,
        })
      );
    } else {
      console.log('Delete Artifact Response', res);
      handleShowToast(messages.MESSAGE.ARTEFACT_DELETED_FAILURE, 'error');
    }
  };

  useEffect(() => {
    if (!props.jobId) {
      dispatch(
        getHypertestJobArtifacts({
          auth,
          jobId,
        })
      );
      if (currentArtefact?.name || props.artefactName) {
        dispatch(
          fetchArtefactContent({
            auth,
            jobId,
            artefactName: props.artefactName || currentArtefact?.name,
          })
        );
      }
      setTimeout(() => {
        const parsed = queryString.parse(window.location.search);
        if (visual.artifectModal) {
          parsed.isArtifactOpen = true;
        }
        if (currentArtefact?.name) {
          parsed.selectedArtifact = currentArtefact?.name;
        }
        const stringified = queryString.stringify(parsed);
        history.push(`?${stringified}`);
      }, 2000);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadArtefactRequest = (name) => {
    dispatch(
      downloadArtefact({
        auth,
        jobId,
        name,
      })
    )
      .then((data) => {
        if (data && data.payload && data.payload.data) {
          window.open(data.payload.data, '_blank');
          sendAmplitudeEvents(EventNames.HYP_DOWNLOAD_ARTEFACT, {
            status: 'success',
          });
        } else {
          sendAmplitudeEvents(EventNames.HYP_DOWNLOAD_ARTEFACT, {
            status: 'fail',
          });
        }
      })
      .catch((error) => {
        sendAmplitudeEvents(EventNames.HYP_DOWNLOAD_ARTEFACT, {
          status: 'fail',
        });
      });
  };
  function handleArtifactRowClick(artefact) {
    const clickableArtefact =
      artefact?.size &&
      artefact?.status === 'completed' &&
      artefact?.removed !== true;
    if (
      !clickableArtefact ||
      !(new Date(artefact.created_at).getTime() > artefactReleaseDate.getTime())
    ) {
      return () => {};
    }
    return () => {
      dispatch(
        fetchArtefactContent({ auth, jobId, artefactName: artefact.name })
      );
      updateArtefactQueryParam(artefact);
    };
  }

  function fetchArtifactsForPage() {
    dispatch(
      fetchArtefactContent({
        auth,
        jobId,
        artefactName: props.artefactName,
        taskID: props.taskID,
        filter: props.filter,
        view: props.view,
      })
    );
    dispatch(
      setCurrentArtefactClick({
        name: props.artefactName,
        id: props.artefactName,
      })
    );
  }

  function handleGoBackClick() {
    updateArtefactQueryParam(null);
  }
  function updateArtefactQueryParam(artefact) {
    const parsed = queryString.parse(window.location.search);
    if (!artefact) {
      delete parsed.selectedArtifact;
    } else {
      parsed.selectedArtifact = artefact?.name;
    }
    const stringified = queryString.stringify(parsed);
    dispatch(setCurrentArtefactClick(artefact));
    history.push(`?${stringified}`);
  }

  function openArtifactInSamePage(downloadLink) {
    /*
      ! If you are making change here,
      Please visit this function as well, it does same thing.
      downloadArtefactLink src/hooks/useFolderStructure.js
    */
    const headers = {
      Authorization:
        'Basic ' +
        Buffer.from(auth.username + ':' + auth.password).toString('base64'),
    };
    fetch(downloadLink, {
      headers,
    })
      .then((res) => {
        return res.blob();
      })
      .then((blob) => {
        var fileURL = window.URL.createObjectURL(blob);
        window.location.replace(fileURL);
      });
  }

  return {
    artifacts,
    getArtifacts,
    deleteArtefact,
    downloadArtefactRequest,
    promoBannerHeight: setting.promoBannerHeight,
    handleArtifactRowClick,
    currentArtefact,
    handleGoBackClick,
    artefactContentStructure,
    fetchArtifactsForPage,
    openArtifactInSamePage,
  };
};

export default useArtifact;
